// @flow
import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';

import { APP_MAX_WIDTH, PAAN_STORE } from '../../constants';
import useFirebase from '../../hooks/firebase';

const useStyles = makeStyles((theme) => ({
	container: {
		width: '100vw',
		maxWidth: APP_MAX_WIDTH,
		height: '84%',
	},
	header: {
		position: 'absolute',
		top: 2,
		left: 6,
		zIndex: 1,
	},
	closeBtn: {
		width: 'auto',
		padding: 0,
		'& .MuiIcon-root': {
			width: 36,
			height: 36,
			fontSize: 30,
			lineHeight: '36px',
		},
	},
	prevBtn: {
		color: '#bedc13',
		fontWeight: 'bold',
		fontSize: 14,
		marginRight: 30,
	},
	nextBtn: {
		borderRadius: '4px',
		backgroundColor: '#bedc13',
		height: '40px',
		width: '100px',
		'&:hover': {
			backgroundColor: '#bedc13',
		},
	},
	mainContainer: {
		height: '80%',
		width: 'inherit',
		maxWidth: 'inherit',
		overflow: 'hidden',
		position: 'relative',
		margin: '-10px 0 12px',
	},
	imgContainer: {
		position: 'absolute',
		width: '100%',
		margin: 'auto',
		transition: 'left 300ms linear',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	btnContainer: {
		justifyContent: 'center',
		// marginTop: 10,
		padding: '0 15px',
		display: 'flex',
		alignItems: 'center',
		flex: 1,
	},
	labelContainer: {
		color: 'white',
		fontSize: 18,
		fontWeight: 'bold',
		textAlign: 'center',
		padding: '0 30px',
		marginBottom: 14,
		flex: 0,
	},
	images: {
		width: '90%',
		margin: 'auto',
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		'& img': {
			width: 'auto',
			// objectFit: 'contain',
			margin: 'auto',
			maxWidth: '100%',
			'&:last-child': {
				border: '3px solid white',
			},
		},
	},
	nextIcon: {
		color: 'black',
		fontSize: '28px',
		lineHeight: '26px',
		width: '14px',
		overflow: 'unset',
	},
}));

const appTips = [
	{
		label: () => {
			return <div>Confirm or Select a machine in app</div>;
		},
		images: ['/images/tip1-machine.png', '/images/tip1.png'],
	},
	{
		label: () => {
			return <div>Select Machine Pickup or Express Delivery</div>;
		},
		images: ['/images/tip2.png'],
	},
	{
		label: () => {
			return <div>Search, Shop and Pay</div>;
		},
		images: ['/images/tip3.png'],
	},
	{
		label: () => {
			return <div>Pick your Machine Order using Access Code</div>;
		},
		images: ['/images/tip4.png'],
	},
	{
		label: () => {
			return (
				<div>
					<div>Next Time just start at</div>
					<div style={{ fontSize: 24 }}>go.vendingbuddy.shop</div>
					<div>on your mobile</div>
				</div>
			);
		},
		images: ['/images/tip5.png'],
	},
];

const affiliateTips = [
	{
		label: () => {
			return <div>Browse your neighbourhood store</div>;
		},
		images: ['/images/affl-tip1.jpg'],
	},
	{
		label: () => {
			return <div>Add your products to cart</div>;
		},
		images: ['/images/affl-tip2.jpg'],
	},
	{
		label: () => {
			return <div> Login with your mobile# and OTP</div>;
		},
		images: ['/images/affl-tip3.jpg'],
	},
	{
		label: () => {
			return <div>Select delivery address and pay</div>;
		},
		images: ['/images/affl-tip4.jpg'],
	},
	{
		label: () => {
			return <div>Order Received! Track your delivery</div>;
		},
		images: ['/images/affl-tip5.jpg'],
	},
];

type Props = {
	onClose: () => void,
	affiliate?: boolean,
	selectedPod: any,
};
export default function Tips({ onClose, affiliate = false, selectedPod = null }: Props) {
	const classes = useStyles();
	const [tipIndex, setTipIndex] = useState(0);
	const [logEvent] = useFirebase();

	const tips = useMemo(
		() => (affiliate || (selectedPod && selectedPod.vm_imei_id === PAAN_STORE) ? affiliateTips : appTips),
		[affiliate, selectedPod]
	);

	const handleTipChange = (val: 'PREV' | 'NEXT') => {
		if (val === 'PREV') {
			setTipIndex((prevTip) => --prevTip);
		}

		if (val === 'NEXT') {
			if (tipIndex >= tips.length - 1) {
				onClose();
				return logEvent('educational_tip_completed');
			}

			setTipIndex((prevTip) => ++prevTip);
		}
	};

	useEffect(() => {
		logEvent('educational_tips_initiated');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<section className={classes.container}>
			<header>
				<IconButton aria-label='close' className={classes.closeBtn} onClick={onClose}>
					<Icon>close</Icon>
				</IconButton>
			</header>
			<main className={classes.mainContainer}>
				{tips.map(({ label, images = [] }, index) => {
					return (
						<figure key={index} className={classes.imgContainer} style={{ left: `${(index - tipIndex) * 100}%` }}>
							<figcaption className={classes.labelContainer}>{label()}</figcaption>
							<section className={classes.images}>
								{images.map((imgSrc, imgIndex) => (
									<img
										key={imgIndex}
										width='400px'
										height={600 / images.length}
										src={imgSrc}
										alt={'tip' + index + 1}
										style={{
											height: `${100 / images.length - 1}%`,
										}}
									/>
								))}
							</section>
						</figure>
					);
				})}
			</main>
			<section className={classes.btnContainer}>
				{tipIndex > 0 && (
					<Button aria-label='prev tip' classes={{ root: classes.prevBtn }} onClick={() => handleTipChange('PREV')}>
						Prev
					</Button>
				)}
				<Button
					variant='contained'
					aria-label='Support'
					classes={{ contained: classes.nextBtn }}
					onClick={() => handleTipChange('NEXT')}
				>
					{tipIndex >= tips.length - 1 ? (
						'Done'
					) : (
						<>
							<span>Next</span>
							<Icon className={classes.nextIcon}>arrow_right</Icon>
						</>
					)}
				</Button>
			</section>
		</section>
	);
}
